// Wire shapes ↔ the app's model types. Renaming and reshaping only: no maths.

import { N } from "@/lib/months";
import type { Definition, EventNode, ModelNode, Lever, Link, MetricNode, Scenario, Worldview } from "@/model/types";
import type {
	DefinitionDto,
	LeverBodyDto,
	LeverDto,
	LinkBodyDto,
	LinkDto,
	NodeDto,
	ResultsDto,
	ScenarioDto,
	WorldviewDto,
	EventSummaryDto,
	MetricSummaryDto,
} from "./types";

export function definitionFromWire(d: DefinitionDto | null | undefined): Definition | undefined {
	if (!d) return undefined;
	const combine = "all" in d.when ? "all" : "any";
	const when = "all" in d.when ? d.when.all : d.when.any;
	return { combine, when: when.map((c) => ({ ...c })), ...(d.at ? { at: d.at } : {}) };
}

export function definitionToWire(d: Definition): DefinitionDto {
	const when = d.combine === "all" ? { all: d.when.map((c) => ({ ...c })) } : { any: d.when.map((c) => ({ ...c })) };
	return d.at ? { when, at: d.at } : { when };
}

/** A wire node. `inputs` come from a worldview node's derived `drivers`; other responses have none (the next worldview load fills them). */
export function nodeFromWire(n: NodeDto, inputs: string[] = []): ModelNode {
	const base = {
		id: n.handle,
		title: n.title,
		icon: n.icon,
		question: n.question,
		resolution: n.resolution,
		key_dates: n.key_dates,
		hero_date: n.hero_date ?? undefined,
		headline_date: n.headline_date,
		visibility: n.visibility,
		tags: n.tags,
		source: n.forecast.source,
		note: n.forecast.note ?? undefined,
		horizon: n.horizon,
		version: n.forecast.version,
		aiDraft: n.forecast.ai_draft,
		pos: n.position ?? undefined,
		inputs,
	};
	if (n.kind === "event") {
		const e: EventNode = {
			...base,
			kind: "event",
			by: n.forecast.by ?? {},
			definition: definitionFromWire(n.definition),
			happened: n.happened ?? null,
			market: n.market ?? undefined,
		};
		return e;
	}
	const m: MetricNode = {
		...base,
		kind: "metric",
		unit: n.unit ?? "",
		scale: n.scale ?? "level",
		range: n.forecast.range ?? {},
		formula: n.forecast.formula ?? undefined,
		observed: n.observations ?? [],
	};
	return m;
}

export function linkFromWire(l: LinkDto): Link {
	if (l.type === "trigger") return { id: l.id, type: "trigger", from: l.from, to: l.to, p: l.p, within_months: l.within_months, lag_months: l.lag_months ?? 0 };
	if (l.type === "impact")
		return { id: l.id, type: "impact", from: l.from, to: l.to, change: l.change, over_months: l.over_months, recovery_months: l.recovery_months ?? null };
	if ("steps" in l) return { id: l.id, type: "multiplier", from: { metric: l.from.metric }, to: l.to, steps: l.steps.map((s) => ({ ...s })) };
	return { id: l.id, type: "multiplier", from: l.from, to: l.to, x: l.x, for_months: l.for_months ?? null };
}

/** A link in the grammar's wire shape (no id), for `POST /links` and `PUT /links/:id`. */
export function linkToWire(l: Link): LinkBodyDto {
	if (l.type === "trigger") return { type: "trigger", from: l.from, to: l.to, p: l.p, within_months: l.within_months, lag_months: l.lag_months };
	if (l.type === "impact") return { type: "impact", from: l.from, to: l.to, change: l.change, over_months: l.over_months, recovery_months: l.recovery_months };
	if ("steps" in l)
		return {
			type: "multiplier",
			from: { metric: l.from.metric },
			to: l.to,
			steps: l.steps.map((s) => (s.from === undefined ? { x: s.x } : { from: s.from, x: s.x })),
		};
	return { type: "multiplier", from: l.from, to: l.to, x: l.x, for_months: l.for_months ?? null };
}

/** The fields a `PATCH /links/:id` may change (never `type`, `from` or `to`). */
export function linkPatchToWire(patch: Partial<Link>): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const k of ["p", "within_months", "lag_months", "x", "for_months", "steps", "change", "over_months", "recovery_months"] as const)
		if (k in patch) out[k] = (patch as Record<string, unknown>)[k];
	return out;
}

/** One lever per node per scenario, so the node's handle is the lever's id. */
export function leverFromWire(l: LeverDto): Lever {
	const node = l.node;
	if ("by" in l) return { id: node, kind: "curve", node, by: l.by };
	if ("range" in l) return { id: node, kind: "range", node, range: l.range };
	if ("happens_by" in l) return { id: node, kind: "event", node, happens: "by", month: l.happens_by, mode: l.mode };
	if ("happens" in l) {
		if (l.happens === true) return { id: node, kind: "event", node, happens: "true", mode: l.mode };
		if (l.happens === false) return { id: node, kind: "event", node, happens: "false", mode: l.mode };
		return { id: node, kind: "event", node, happens: "at", month: l.happens, mode: l.mode };
	}
	// `in` never comes back from the API (it is stored as `by`).
	return { id: node, kind: "curve", node, by: {} };
}

export function leverToWire(l: Lever): LeverBodyDto {
	if (l.kind === "curve") return { by: l.by };
	if (l.kind === "range") return { range: l.range };
	if (l.happens === "by") return { happens_by: l.month ?? "2027-06", mode: l.mode };
	if (l.happens === "at") return { happens: l.month ?? "2027-06", mode: l.mode };
	return { happens: l.happens === "true", mode: l.mode };
}

export function scenarioFromWire(s: ScenarioDto): Scenario {
	return { id: s.id, slug: s.slug, name: s.name, description: s.description ?? undefined, levers: s.levers.map(leverFromWire) };
}

export function worldFromWire(w: WorldviewDto): Worldview {
	const nodes: Record<string, ModelNode> = {};
	for (const n of w.nodes)
		nodes[n.handle] = nodeFromWire(
			n,
			n.drivers.filter((d) => d.via.some((v) => v.type !== "link")).map((d) => d.node),
		);
	return { nodes, links: w.links.map(linkFromWire), scenarios: w.scenarios.map(scenarioFromWire) };
}

// ─── Results ────────────────────────────────────────────────────────────────────

/** One run, indexed like the chart grid (index 0 = the month after now). */
export interface RunResult {
	events: Record<string, { F: number[]; summary: EventSummaryDto | null }>;
	metrics: Record<string, { q: [number[], number[], number[], number[], number[]]; mean: number[]; summary: MetricSummaryDto | null }>;
	probability: number | null;
	hypothetical: boolean;
	impossible: boolean;
	errors: Record<string, string>;
	paths: number;
}

/** Hold the last value to the end of the chart grid (series stop at a node's horizon). */
export function padded(a: number[], n = N): number[] {
	if (a.length >= n || a.length === 0) return a;
	const out = a.slice();
	const last = a[a.length - 1];
	while (out.length < n) out.push(last);
	return out;
}

export function resultFromWire(r: ResultsDto): RunResult {
	const events: RunResult["events"] = {};
	for (const [h, e] of Object.entries(r.events)) events[h] = { F: padded(e.chance_by), summary: e.summary?.kind === "event" ? e.summary : null };
	const metrics: RunResult["metrics"] = {};
	for (const [h, m] of Object.entries(r.metrics))
		metrics[h] = {
			q: [padded(m.p10), padded(m.p25), padded(m.p50), padded(m.p75), padded(m.p90)],
			mean: padded(m.mean),
			summary: m.summary?.kind === "metric" ? m.summary : null,
		};
	return { events, metrics, probability: r.probability, hypothetical: r.hypothetical, impossible: r.impossible, errors: r.errors, paths: r.paths };
}
